import TextField from "./WorksheetTextField";
import { worksheetHeaderFieldSx } from "./worksheetStyles";
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  ButtonBase,
  Collapse,
  FormControlLabel,
  IconButton,
  Tooltip,
  Switch,
  MenuItem,
  Menu,
  Typography,
} from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useQuery } from "@apollo/client";
import UiCard from "components/base/UiCard";
import UiButton from "components/base/UiButton";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import WorksheetScopeEditor, { WorksheetScopeEditorHandle } from "./WorksheetScopeEditor";
import WorksheetProductTypeSelect from "./WorksheetProductTypeSelect";
import WorksheetCostSection from "./WorksheetCostSection";
import {
  worksheetFieldSx,
  worksheetNumberProps,
  worksheetSellingGrid,
  worksheetCalculatedFieldSx,
} from "./worksheetStyles";
import { renderWorksheetScopeHtml, scopePlaceholders } from "./scope";
import { SETTINGS } from "./api";
import {
  Worksheet,
  CostRow,
  worksheetFinancials,
  decimalDollars,
  formatDollarAmount,
  priceBeforeDiscount,
  pricingFromTarget,
  capturePricingBaseline,
  resetWorksheetPricing,
  money,
  worksheetInput,
  sellingUnits,
  sellingUnitLabel,
  measurementUnit,
} from "./calculator";

type Props = {
  initial: Worksheet;
  onSave: (item: Worksheet, discountCents?: string) => Promise<void>;
  discountCents?: string;
  allowDiscount?: boolean;
  sharedColumnHeaders?: boolean;
  readOnly?: boolean;
  onSaveProduct?: (item: Worksheet, productTypeId: string) => Promise<void>;
  saveLabel?: string;
  actions?: (close: () => void) => React.ReactNode;
  optionalControl?: React.ReactNode;
  onDirtyChange?: (dirty: boolean) => void;
  registerSave?: (save: () => Promise<{ status: boolean; data: null }>) => void;
};
// An unfinished value in an inactive pricing field must not block the new method.
const retainedPricingValue = (value: string) =>
  /^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(value) ? value : "0";

export default function WorksheetEditor({
  initial,
  onSave,
  readOnly = false,
  onSaveProduct,
  saveLabel = "Save line item",
  onDirtyChange,
  registerSave,
  actions,
  optionalControl,
  discountCents = "0",
  allowDiscount = false,
  sharedColumnHeaders = false,
}: Props) {
  const scopeRef = useRef<WorksheetScopeEditorHandle | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [item, setItem] = useState<Worksheet>(() => worksheetInput(initial));
  const [metricTooltip, setMetricTooltip] = useState<string | null>(null);
  const [profitDraft, setProfitDraft] = useState<string | null>(null);
  const [marginDraft, setMarginDraft] = useState<string | null>(null);
  const [pricingError, setPricingError] = useState("");
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [editingTotal, setEditingTotal] = useState(false);
  const [totalDraft, setTotalDraft] = useState<string | null>(null);
  const [discount, setDiscount] = useState(() => decimalDollars(discountCents));
  useEffect(() => {
    if (!dirty) setDiscount(decimalDollars(discountCents));
  }, [discountCents]);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const { data: settings } = useQuery(SETTINGS);
  const change = (patch: Partial<Worksheet>) => {
    setItem((old) => ({ ...old, ...patch }));
    setDirty(true);
    setNotice("");
  };
  const insertScopePlaceholder = (token: string) => scopeRef.current?.insert(token);
  const rowChange = (index: number, patch: Partial<CostRow>) =>
    change({ rows: item.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  let totals: ReturnType<typeof worksheetFinancials> | undefined;
  let calculationError = "";
  try {
    totals = worksheetFinancials(item, discount);
  } catch (e) {
    calculationError = (e as Error).message;
  }
  const setPricingTarget = (target: "profit" | "margin", raw: string) => {
    setNotice("");
    const value = raw.replace(/[$,%]/g, "");
    if (target === "profit") {
      setProfitDraft(value);
      setMarginDraft(null);
    } else {
      setMarginDraft(value);
      setProfitDraft(null);
    }
    setTotalDraft(null);
    try {
      change({
        ...pricingFromTarget(item, discount, target, value),
        pricingOverrideField: target,
        pricingBaseline: capturePricingBaseline(item),
      });
      setPricingError("");
    } catch (e) {
      setPricingError((e as Error).message);
      setBodyExpanded(true);
      setDirty(true);
    }
  };
  const derivedRate =
    totals && Number(item.quantity) > 0
      ? (item.pricingMethod === "cost_markup" ? totals.price : totals.basePrice)
          .div(100)
          .div(item.quantity)
          .toDecimalPlaces(8)
          .toString()
      : "";
  const changeCostPricing = (enabled: boolean) => {
    if (!totals) return;
    setTotalDraft(null);
    change({ pricingBaseline: null, pricingOverrideField: "none" });
    setProfitDraft(null);
    setMarginDraft(null);
    setPricingError("");
    change(
      enabled
        ? { pricingMethod: "cost_markup" }
        : {
            pricingMethod: "fixed",
            fixedPrice: totals.price.div(100).toFixed(2),
            profitAmount: "0",
          }
    );
    if (enabled) setExpanded(true);
  };
  const incomplete =
    !item.name.trim() ||
    item.rows.some((row) => !row.name.trim() || !row.unit.trim()) ||
    !item.unit.trim();
  const [productDialog, setProductDialog] = useState(false);
  const [productTypeId, setProductTypeId] = useState("");
  const save = async (asProduct = false, categoryId?: string) => {
    if (busy || readOnly || incomplete || calculationError || pricingError)
      return { status: false, data: null };
    if (asProduct && !categoryId) {
      setProductDialog(true);
      return { status: false, data: null };
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (asProduct) {
        await onSaveProduct!(worksheetInput(item), categoryId!);
        setProductDialog(false);
        setProductTypeId("");
      } else await onSave(worksheetInput(item), totals?.discount.toFixed(0));
      if (!asProduct) {
        setDirty(false);
        setItem((old) => ({
          ...old,
          rows: old.rows.map((row) =>
            row.truckCountDefault ? { ...row, truckCountDefault: false } : row
          ),
        }));
      }
      setNotice(
        asProduct
          ? "Saved under the selected Product Type. This estimate remains independent."
          : "Saved."
      );
      return { status: true, data: null };
    } catch (e) {
      setBodyExpanded(true);
      setError((e as Error).message || "Unable to save. Your changes are still here; try again.");
      return { status: false, data: null };
    } finally {
      setBusy(false);
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    registerSave?.(() => saveRef.current());
  }, [registerSave]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, item, onDirtyChange]);
  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange]
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const field = (label: string, key: keyof Worksheet, numeric = false) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={item[key] ?? ""}
      disabled={readOnly || busy}
      type={numeric ? "number" : "text"}
      inputProps={numeric ? worksheetNumberProps : undefined}
      onChange={(e) => change({ [key]: e.target.value })}
    />
  );
  const move = (index: number, offset: number) => {
    const rows = [...item.rows];
    [rows[index], rows[index + offset]] = [rows[index + offset], rows[index]];
    change({ rows });
  };
  const addRow = (category: CostRow["category"]) =>
    change({
      rows: [
        ...item.rows,
        {
          name: "",
          category,
          quantity: "1",
          unit: category === "labor" ? "Hours" : category === "trucking" ? "Hours" : "Each",
          ...(category === "trucking"
            ? { truckingUnit: "Hours" as const, trucksNeeded: "1", truckCountDefault: true }
            : {}),
          rate: "0",
          calculation: "manual",
        },
      ],
    });
  return (
    <UiCard variant="section" sx={{ mb: 1.5, ...worksheetFieldSx }}>
      <Dialog
        open={productDialog}
        onClose={() => !busy && setProductDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Save as product</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          {productDialog && (
            <WorksheetProductTypeSelect
              value={productTypeId}
              onChange={setProductTypeId}
              disabled={busy}
            />
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <UiButton uiVariant="outlined" disabled={busy} onClick={() => setProductDialog(false)}>
            Cancel
          </UiButton>
          <UiButton
            uiVariant="primary-blue"
            disabled={busy || !productTypeId}
            onClick={() => save(true, productTypeId)}
          >
            Save product
          </UiButton>
        </DialogActions>
      </Dialog>
      <Box
        component="header"
        sx={{
          ...worksheetHeaderFieldSx,
          p: 1.5,
          ...(sharedColumnHeaders
            ? {
                py: { xs: 1.5, lg: 1 },
                "& .MuiInputLabel-root": { display: { xs: "block", lg: "none" } },
                "& .MuiOutlinedInput-notchedOutline": { top: { lg: 0 } },
                "& .MuiOutlinedInput-notchedOutline legend": { display: { lg: "none" } },
              }
            : {}),
          bgcolor: draftColors.surfaceMuted,
          borderBottom: `1px solid ${draftColors.border}`,
        }}
      >
        <Box
          sx={{
            ...worksheetSellingGrid,
            "& > :not(:first-child):not(:nth-child(3)) input": { textAlign: "right" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <IconButton
              size="small"
              sx={{ flexShrink: 0 }}
              aria-label={bodyExpanded ? "Collapse line item" : "Expand line item"}
              aria-expanded={bodyExpanded}
              onClick={() => setBodyExpanded(!bodyExpanded)}
            >
              <ExpandMore
                sx={{ fontSize: 20, transform: bodyExpanded ? "none" : "rotate(-90deg)" }}
              />
            </IconButton>
            {field("Item Name", "name")}
          </Box>
          {field("Quantity", "quantity", true)}
          <TextField
            select
            size="small"
            fullWidth
            label="Unit"
            SelectProps={{ renderValue: (value) => sellingUnitLabel(String(value)) }}
            value={item.unit}
            disabled={readOnly || busy}
            onChange={(e) => {
              const unit = e.target.value;
              const measured = measurementUnit(unit);
              change({
                unit,
                areaSource: measured && measured !== "lin_ft" ? "selling_quantity" : "manual",
                rows: item.rows.map((row) =>
                  row.calculation === "coverage"
                    ? {
                        ...row,
                        factorAreaUnit:
                          measured === "lin_ft"
                            ? "lin_ft"
                            : row.factorAreaUnit === "lin_ft"
                            ? "sq_ft"
                            : row.factorAreaUnit,
                      }
                    : row
                ),
              });
            }}
          >
            {!sellingUnits.includes(item.unit) && (
              <MenuItem value={item.unit} disabled>
                {sellingUnitLabel(item.unit)}
              </MenuItem>
            )}
            {sellingUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {sellingUnitLabel(unit)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            fullWidth
            label="Unit Price ($)"
            type="text"
            inputProps={{ inputMode: "decimal" }}
            InputProps={{ readOnly: item.pricingMethod === "cost_markup" }}
            disabled={readOnly || busy}
            value={item.pricingMethod === "selling_rate" ? item.rate : derivedRate}
            FormHelperTextProps={{ sx: { color: draftColors.textSecondary, fontSize: 11, mx: 0 } }}
            onChange={(e) =>
              change({
                pricingMethod: "selling_rate",
                pricingOverrideField: "none",
                rate: e.target.value,
                pricingBaseline: item.pricingBaseline
                  ? { ...item.pricingBaseline, pricingMethod: "selling_rate", profitAmount: null }
                  : null,
                fixedPrice: retainedPricingValue(item.fixedPrice),
                markup: retainedPricingValue(item.markup),
              })
            }
          />
          {(["overheadPercentage", "indirectPercentage"] as const).map((field) => (
            <Tooltip
              key={field}
              title={
                totals
                  ? `${field === "overheadPercentage" ? "Overhead" : "Indirect"}: ${money(
                      field === "overheadPercentage" ? totals.overhead : totals.indirect
                    )}`
                  : "Enter valid pricing inputs to calculate this cost."
              }
              open={metricTooltip === field}
              onOpen={() => setMetricTooltip(field)}
              onClose={() => setMetricTooltip(null)}
              disableTouchListener
            >
              <TextField
                size="small"
                fullWidth
                label={field === "overheadPercentage" ? "Overhead %" : "Indirect %"}
                value={item[field] ?? "0"}
                error={!/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(item[field] ?? "0")}
                helperText={
                  !/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(item[field] ?? "0")
                    ? "Enter a nonnegative number."
                    : undefined
                }
                disabled={readOnly || busy}
                inputProps={{ inputMode: "decimal" }}
                onFocus={() => setMetricTooltip(field)}
                onClick={() => setMetricTooltip(field)}
                onBlur={() => setMetricTooltip(null)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setMetricTooltip(null);
                }}
                onChange={(e) => change({ [field]: e.target.value })}
              />
            </Tooltip>
          ))}
          <Tooltip
            title="Direct costs + overhead + indirect."
            open={metricTooltip === "costs"}
            onOpen={() => setMetricTooltip("costs")}
            onClose={() => setMetricTooltip(null)}
            disableTouchListener
          >
            <TextField
              size="small"
              fullWidth
              label="Costs ($)"
              onFocus={() => setMetricTooltip("costs")}
              onClick={() => setMetricTooltip("costs")}
              onBlur={() => setMetricTooltip(null)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setMetricTooltip(null);
              }}
              sx={worksheetCalculatedFieldSx}
              value={totals ? formatDollarAmount(totals.cost.div(100).toFixed(2)) : "—"}
              InputProps={{ readOnly: true }}
            />
          </Tooltip>
          <Tooltip
            title={
              item.pricingMethod === "cost_markup"
                ? "Enter the profit after discounts; markup updates automatically"
                : "Enter the profit after discounts to calculate the selling total"
            }
          >
            <TextField
              size="small"
              fullWidth
              label="Profit ($)"
              overridden={item.pricingOverrideField === "profit"}
              error={!!pricingError && profitDraft !== null}
              type="text"
              inputProps={{ inputMode: "decimal" }}
              value={
                profitDraft !== null
                  ? profitDraft
                  : totals?.profit != null
                  ? formatDollarAmount(totals.profit.div(100).toFixed(2))
                  : totals
                  ? "0.00"
                  : "—"
              }
              disabled={readOnly || busy}
              onFocus={() => setProfitDraft(totals?.profit?.div(100).toFixed(2) ?? "0")}
              onBlur={() => {
                if (!pricingError) setProfitDraft(null);
              }}
              onChange={(e) => setPricingTarget("profit", e.target.value)}
              sx={{
                "& input": { color: totals?.profit?.isNegative() ? draftColors.red : undefined },
              }}
            />
          </Tooltip>
          <TextField
            size="small"
            fullWidth
            label="Margin (%)"
            overridden={item.pricingOverrideField === "margin"}
            error={!!pricingError && marginDraft !== null}
            value={
              marginDraft !== null
                ? marginDraft
                : totals?.marginPercentage != null
                ? `${totals.marginPercentage.toFixed(2)}%`
                : totals
                ? "0.00%"
                : "—"
            }
            disabled={readOnly || busy}
            inputProps={{ inputMode: "decimal" }}
            onFocus={() => setMarginDraft(totals?.marginPercentage?.toFixed(2) ?? "0")}
            onBlur={() => {
              if (!pricingError) setMarginDraft(null);
            }}
            onChange={(e) => setPricingTarget("margin", e.target.value)}
            sx={{
              "& input": { color: totals?.profit?.isNegative() ? draftColors.red : undefined },
            }}
          />
          <TextField
            size="small"
            fullWidth
            label="Discount ($)"
            type="text"
            inputProps={{ inputMode: "decimal" }}
            value={editingDiscount ? discount : formatDollarAmount(discount)}
            onFocus={() => setEditingDiscount(true)}
            onBlur={() => setEditingDiscount(false)}
            disabled={readOnly || busy || !allowDiscount}
            onChange={(e) => {
              setDiscount(e.target.value.replace(/[$,]/g, ""));
              setTotalDraft(null);
              setDirty(true);
              setNotice("");
            }}
          />
          <TextField
            size="small"
            fullWidth
            label="Total ($)"
            overridden={item.pricingOverrideField === "total"}
            type="text"
            inputProps={worksheetNumberProps}
            disabled={readOnly || busy}
            value={
              editingTotal
                ? totalDraft !== null
                  ? totalDraft
                  : totals?.total.div(100).toFixed(2) ?? ""
                : formatDollarAmount(
                    totalDraft !== null ? totalDraft : totals?.total.div(100).toFixed(2) ?? ""
                  )
            }
            onFocus={() => setEditingTotal(true)}
            onBlur={() => {
              setEditingTotal(false);
              if (totals) setTotalDraft(null);
            }}
            FormHelperTextProps={{ sx: { color: draftColors.textSecondary, fontSize: 11, mx: 0 } }}
            onChange={(e) => {
              const value = e.target.value.replace(/[$,]/g, "");
              setTotalDraft(value);
              try {
                const profitAmount =
                  item.pricingMethod === "cost_markup" ? "0" : item.profitAmount ?? "0";
                const fixedPrice = priceBeforeDiscount(value, discount, profitAmount);
                change({
                  pricingMethod: "fixed",
                  pricingOverrideField: "total",
                  fixedPrice,
                  profitAmount,
                  pricingBaseline: capturePricingBaseline(item),
                  rate: retainedPricingValue(item.rate),
                  markup: retainedPricingValue(item.markup),
                });
                setPricingError("");
              } catch (e) {
                setPricingError((e as Error).message);
                setDirty(true);
                setBodyExpanded(true);
              }
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", lg: "center" },
              minHeight: 30,
            }}
          >
            {optionalControl}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              minHeight: 30,
              gridColumn: { xs: "1 / -1", lg: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            {dirty && (
              <Tooltip title="Unsaved changes — expand the item to save">
                <Box
                  component="span"
                  aria-label="Unsaved changes"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: draftColors.blue,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            {!readOnly && (actions || item.pricingBaseline) && (
              <>
                <IconButton
                  size="small"
                  disabled={busy}
                  aria-label={`Actions for ${item.name}`}
                  onClick={(event) => setMenuAnchor(event.currentTarget)}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                  {item.pricingBaseline && (
                    <MenuItem
                      onClick={() => {
                        const restored = resetWorksheetPricing(item);
                        change(restored);
                        try {
                          worksheetFinancials(restored, discount);
                        } catch {
                          setBodyExpanded(true);
                        }
                        setProfitDraft(null);
                        setMarginDraft(null);
                        setTotalDraft(null);
                        setPricingError("");
                        setError("");
                        setMenuAnchor(null);
                      }}
                    >
                      Reset pricing
                    </MenuItem>
                  )}
                  {actions?.(() => setMenuAnchor(null))}
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Collapse in={bodyExpanded} unmountOnExit={false}>
        <Box sx={{ px: 1, py: 0.5 }}>
          <UiCard variant="inner" sx={{ p: 0.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Scope of Work</Typography>
              {!readOnly && (
                <TextField
                  select
                  size="small"
                  value=""
                  disabled={busy}
                  sx={{ width: 170 }}
                  SelectProps={{
                    displayEmpty: true,
                    SelectDisplayProps: { "aria-label": "Insert scope placeholder" },
                    renderValue: () => "Insert placeholder",
                    MenuProps: {
                      disableRestoreFocus: true,
                      TransitionProps: { onExited: () => scopeRef.current?.focus() },
                    },
                  }}
                  onChange={(e) => insertScopePlaceholder(e.target.value)}
                >
                  {scopePlaceholders.map(({ label, token }) => (
                    <MenuItem key={token} value={token}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            <WorksheetScopeEditor
              ref={scopeRef}
              value={item.scope ?? ""}
              format={item.scopeFormat}
              disabled={readOnly || busy}
              onChange={(scope) => change({ scope, scopeFormat: "html" })}
            />
            {scopePlaceholders.some(({ token }) => item.scope?.includes(token)) && (
              <Box sx={{ mt: 0.5, p: 1, bgcolor: draftColors.surfaceMuted, borderRadius: 1 }}>
                <Typography sx={{ fontSize: 11, color: draftColors.textSecondary }}>
                  Statement of work preview
                </Typography>
                <Box
                  sx={{
                    fontSize: 12,
                    overflowWrap: "anywhere",
                    "& p": { m: 0 },
                    "& ul": { my: 0, pl: 2.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: renderWorksheetScopeHtml(item) }}
                />
              </Box>
            )}
          </UiCard>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            bgcolor: draftColors.surfaceMuted,
            borderTop: `1px solid ${draftColors.border}`,
            borderBottom: `1px solid ${draftColors.border}`,
            mb: 0.5,
            flexWrap: "wrap",
          }}
        >
          <ButtonBase
            aria-label="Cost Breakdown"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            sx={{
              flex: 1,
              justifyContent: "flex-start",
              gap: 0.75,
              py: 0.5,
              minWidth: 160,
              borderRadius: 1,
              "&.Mui-focusVisible": { outline: `2px solid ${draftColors.blue}`, outlineOffset: -2 },
            }}
          >
            <ExpandMore sx={{ fontSize: 20, transform: expanded ? "rotate(180deg)" : "none" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Cost Breakdown</Typography>
          </ButtonBase>
          <FormControlLabel
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
            label="Calculate selling price from costs"
            control={
              <Switch
                size="small"
                checked={item.pricingMethod === "cost_markup"}
                disabled={readOnly || busy || !totals}
                onChange={(_, checked) => changeCostPricing(checked)}
              />
            }
          />
        </Box>
        <Collapse in={expanded} unmountOnExit={false}>
          <Box sx={{ p: 0.5, display: "grid", gap: 0.5, bgcolor: draftColors.pageBg }}>
            {(["labor", "material", "trucking", "equipment", "other"] as const).map((category) => (
              <WorksheetCostSection
                key={category}
                category={category}
                item={item}
                onItemChange={change}
                rows={item.rows}
                totals={totals}
                readOnly={readOnly}
                busy={busy}
                settings={settings}
                onChange={rowChange}
                onAdd={() => addRow(category)}
                onMove={move}
                onCopy={(index) =>
                  change({
                    rows: [
                      ...item.rows.slice(0, index + 1),
                      { ...item.rows[index] },
                      ...item.rows.slice(index + 1),
                    ],
                  })
                }
                onDelete={(index) => change({ rows: item.rows.filter((_, i) => i !== index) })}
              />
            ))}
          </Box>
        </Collapse>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0, display: "grid", gap: 1 }}>
          {calculationError && <Alert severity="warning">{calculationError}</Alert>}
          {pricingError && <Alert severity="warning">{pricingError}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {!readOnly && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <UiButton
                uiVariant="primary-green"
                disabled={busy || incomplete || !!calculationError || !!pricingError}
                onClick={() => save()}
              >
                {busy ? "Saving…" : saveLabel}
              </UiButton>
              {onSaveProduct && (
                <UiButton
                  uiVariant="outlined-small"
                  disabled={busy || incomplete || !!calculationError || !!pricingError}
                  onClick={() => save(true)}
                >
                  Save as product
                </UiButton>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </UiCard>
  );
}
